import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation, NavigationLink } from 'ui-library';
import { getSetupData } from 'additiv-services/setup';
import { useManagerProfile } from '../../prodivers/managerProfile';

// Assuming getSetupData returns an object with this structure
interface SetupData {
    IFRAME_CASE_MANAGEMENT_URL: string;
    [key: string]: any;
}

const setupData = getSetupData() as SetupData;

interface SideNavigationProps {
    /** Controls visibility of the navigation drawer */
    isOpen?: boolean;
    /** Current active route path used to highlight the home link */
    current: string;
    /** Callback fired when the navigation needs to be closed */
    onClose?: () => void;
}

const SideNavigation: FC<SideNavigationProps> = ({
    isOpen = false,
    current,
    onClose = () => {},
}) => {
    const { t } = useTranslation();

    const additivCreditIframeUrl = setupData.CREDIT_APP_URL;
    const ziagCreditIframeUrl = setupData.CREDIT_APP_ZIAG_URL;
    const isZurichInvest = window.location.href.includes('zurich.ch');

    const creditAppIframeURL = useMemo(() => `${isZurichInvest ? ziagCreditIframeUrl : additivCreditIframeUrl}/credit-requests`, [isZurichInvest]);

    const { hasAccessPolicy } = useManagerProfile();

    // Memoizing the URL to ensure stability across re-renders
    const iframeURL = useMemo(() => setupData.IFRAME_CASE_MANAGEMENT_URL, []);

    return (
        <Navigation isOpen={isOpen} onClose={onClose}>
            <NavigationLink to="/dashboard" homeLink={current === '/dashboard'}>
                {t('navigation.sideNav.dashboard')}
            </NavigationLink>

            <NavigationLink
                to="/clients"
                homeLink={current.includes('clients')}
            >
                {t('navigation.sideNav.clients')}
            </NavigationLink>
            {hasAccessPolicy('EnableDocumentLibraryTab') && (
                <NavigationLink to="/documents" target="_blank" rel="noopener noreferrer">
                    {t('navigation.sideNav.documents')}
                </NavigationLink>
            )}
            {hasAccessPolicy('CaseManagement') && (
                <NavigationLink
                    externalURL
                    to={{ pathname: iframeURL }}
                    target="_blank"
                    rel="noreferrer"
                >
                    {t('navigation.sideNav.caseManagement')}
                </NavigationLink>
            )}
            {hasAccessPolicy('EnableGeneralReports') && (
                <NavigationLink to="/reports" homeLink={current === '/reports'}>
                    {t('navigation.sideNav.reports')}
                </NavigationLink>
            )}
            {setupData?.CREDIT_INTEGRATION_FEATURE_FLAG && hasAccessPolicy('NavigateFromWealthToCredit') && (
                <NavigationLink
                    externalURL
                    to={{ pathname: creditAppIframeURL }}
                    target="_blank"
                    rel="noreferrer"
                >
                    {t('navigation.sideNav.CreditAppNavigation')}
                </NavigationLink>
            )}
        </Navigation>
    );
};

export default SideNavigation;
